'use client';

import { useRouter, usePathname } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';

import { resetPasswordSchema, type ResetPasswordFormData } from '@/lib/validations';
import { api } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '@/components/ui/form';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';

export function ResetPasswordForm() {
  const router = useRouter();
  const pathname = usePathname();
  const [error, setError] = useState<string>();
  const [confirmMessage, setConfirmMessage] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  // Extract token from URL pathname (e.g. /reset/abc123/ -> abc123)
  const [token] = useState<string | undefined>(() => {
    if (pathname) {
      const pathParts = pathname.split('/').filter(Boolean);
      if (pathParts.length >= 2 && pathParts[0] === 'reset') {
        return pathParts[1];
      }
    }
    return undefined;
  });

  const form = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      password: '',
      password2: '',
    },
  });

  const onSubmit = async (data: ResetPasswordFormData) => {
    setError(undefined);
    setConfirmMessage('');
    setIsLoading(true);

    // Validate token exists
    if (!token) {
      setError('Invalid or missing reset token. Please request a new password reset.');
      setIsLoading(false);
      return;
    }

    try {
      const { response, result } = await api.resetPassword(data.password, token);

      if (!response.ok) {
        // Handle API error response
        const errorResult = result as { error?: string; errors?: string[]; message?: string };
        if (errorResult?.error) {
          setError(errorResult.error);
        } else if (errorResult?.errors) {
          setError(errorResult.errors.join('\n'));
        } else if (errorResult?.message) {
          setError(errorResult.message);
        } else {
          setError('Failed to reset password. Please try again or request a new reset link.');
        }
        setIsLoading(false);
        return;
      }

      // Success - show confirmation
      setConfirmMessage('Your password has been reset successfully.');
      setIsLoading(false);
    } catch (err) {
      console.error('[ResetPasswordForm] Error:', err);
      setError('An error occurred. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col">
      <div className="flex flex-1 flex-col items-center justify-center p-4">
        <div className="mb-8">
          <Link href="/">
            <Image
              src="/images/Embrace_for_Good_Black_Logo.png"
              alt="Lincoln Embrace for Good"
              width={400}
              height={120}
              priority
            />
          </Link>
        </div>

        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Reset Password</CardTitle>
            <CardDescription>Enter your new password below.</CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription className="whitespace-pre-line">{error}</AlertDescription>
              </Alert>
            )}

            {confirmMessage && (
              <Alert className="mb-4">
                <AlertDescription>{confirmMessage}</AlertDescription>
              </Alert>
            )}

            {!confirmMessage && (
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>New Password</FormLabel>
                        <FormControl>
                          <Input type="password" autoComplete="new-password" {...field} />
                        </FormControl>
                        <FormDescription>At least 6 characters</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="password2"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Confirm New Password</FormLabel>
                        <FormControl>
                          <Input type="password" autoComplete="new-password" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? 'Resetting...' : 'Reset Password'}
                  </Button>
                </form>
              </Form>
            )}

            {confirmMessage && (
              <Button className="w-full" onClick={() => router.push('/login')}>
                Go to Login
              </Button>
            )}

            {!confirmMessage && (
              <div className="mt-4 space-y-2">
                <Link href="/login" className="block text-sm text-primary hover:underline">
                  Back to Log In
                </Link>
                <Link
                  href="/password"
                  className="block text-sm text-muted-foreground hover:underline"
                >
                  Request New Reset Link
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Footer links */}
      <footer className="py-4 text-center text-sm text-muted-foreground">
        <div className="flex justify-center gap-4">
          <Link href="/contact" className="hover:underline">
            Contact Us
          </Link>
          <Link href="/terms-eligibility" className="hover:underline">
            Program Terms and Conditions
          </Link>
          <Link href="/resources" className="hover:underline">
            Program Resources
          </Link>
        </div>
      </footer>
    </div>
  );
}
